import React, { useState, useEffect } from 'react';

// Utils
import { SortDirection } from 'ka-table/enums';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import PermissionsGate, { isGranted } from '../../../../../utils/gate/permissionsGate';
import { useAppContext } from '../../../../../store/AppContext';
import SCOPE from '../../../../../utils/gate/scope';
import languages from '../../../../../constants/languages';
import useNotification from '../../../../../utils/hooks/useNotification';

// Icons
import { ReactComponent as PlusIcon } from '../../../../../assets/icons/plus.svg';

// Services
import useExtraService from '../../../../../services/extraService';

// Components
import SkeletonLoader from '../../../../molecules/SkeletonLoader/SkeletonLoader';
import GenericTable from '../../../../tables/GenericTable/GenericTable';
import EmptyList from '../../../../organisms/EmptyList/EmptyList';
import DropdownActions from '../../../../molecules/DropdownActions/DropdownActions';
import Button from '../../../../atoms/Button/Button';

function Extras() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const location = useLocation();
  const navigate = useNavigate();
  const [{ user }] = useAppContext();

  const { displayNotification } = useNotification();

  const { getAllExtras } = useExtraService();

  const [extras, setExtras] = useState([]);

  useEffect(() => {
    if (location?.state) {
      const notif = location.state;
      displayNotification({ message: t(notif) });
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const { isFetching } = useQuery(
    ['getAllExtras'],
    () => getAllExtras(),
    {
      onSuccess: (res) => setExtras(res?.data?.extras || []),
    },
  );

  const columns = {
    name: {
      key: 'name', title: t('extras.table.columns.name'), sortDirection: SortDirection.Ascend, bold: true,
    },
    title: { key: 'title', title: t('extras.table.columns.title') },
    actions: { key: 'actions', title: t('extras.table.columns.actions') },
  };

  const getExtraRow = (extra, index) => ({
    id: index,
    name: extra.name,
    title: extra.translations.find((tr) => tr.language === languages.en_US)?.title,
    actions: (
      <div className="d-flex mobile-d-col mobile-a-end f-wrap">
        <DropdownActions
          id={`extrasActionsEdit${extra.id}`}
          rightAlign
          label={t('extras.table.actions.edit.label')}
          options={[
            {
              label: t('extras.table.actions.edit.options.marketing'),
              href: `/products/extras/marketing?id=${extra.id}`,
            },
            {
              label: t('extras.table.actions.edit.options.logistic'),
              href: `/products/extras/logistic?id=${extra.id}`,
            },
          ]}
          className="m-r-30 mobile-m-r-0"
        />

        <DropdownActions
          id={`extrasActionsTranslate${extra.id}`}
          rightAlign
          label={t('extras.table.actions.translate.label')}
          options={[
            {
              label: t('extras.table.actions.translate.options.french'),
              href: `/products/extras/translate?id=${extra.id}&lang=${languages.fr_FR}`,
            },
            {
              label: t('extras.table.actions.translate.options.english'),
              href: `/products/extras/translate?id=${extra.id}&lang=${languages.en_US}`,
            },
          ]}
        />
      </div>
    ),
  });

  return (
    (isFetching) ? (
      <>
        <SkeletonLoader height={60} className="m-b-20" />
        <SkeletonLoader height={300} />
      </>
    ) : (
      <>
        {extras?.length ? (
          <GenericTable
            columns={[columns.name, columns.title, columns.actions]}
            rows={extras.map((extra, index) => getExtraRow(extra, index))}
            isSearchable
            searchPlaceholder={t('extras.table.search')}
          />
        ) : (
          <EmptyList
            imageTheme="products"
            title={t('extras.table.empty.title')}
            text={`
              ${t('extras.table.empty.text')} 
              ${isGranted(user, [SCOPE.create_ownEst_camp]) ? t('extras.table.empty.add') : ''}
            `}
          />
        )}

        {!isFetching ? (
          <PermissionsGate scopes={[SCOPE.create_ownEst_extra]}>
            <div className="d-flex a-center j-end sticky-actions mobile-a-stretch">
              <a href="/products/extras/new">
                <Button full={isMobile}>
                  <PlusIcon className="m-r-10" />
                  {t('extras.add')}
                </Button>
              </a>
            </div>
          </PermissionsGate>
        ) : null}
      </>
    )
  );
}

Extras.propTypes = {};

export default Extras;
